<template>
  <v-card class="pm-panel" elevation="0">
    <!-- Header -->
    <div class="pm-header">
      <div class="pm-search-badge">
        <v-icon color="white" large>mdi-magnify</v-icon>
      </div>
      <div class="pm-header-text">
        <h2 class="pm-title">{{ title }}</h2>
        <p class="pm-subtitle">{{ subtitle }}</p>
      </div>
    </div>

    <!-- Cards grid -->
    <div class="pm-grid">
      <template v-for="(item, i) in items">
        <div v-if="viewPermissions(item.roles)" :key="i" class="pm-card">
          <div class="pm-card-body">
            <div class="pm-icon-badge">
              <v-img :src="item.src" width="34" height="34" contain />
            </div>
            <div class="pm-card-text">
              <h3 class="pm-card-heading">{{ item.heading }}</h3>
              <span class="pm-card-divider" />
              <p class="pm-card-desc">{{ item.text }}</p>
            </div>
          </div>
          <router-link :to="item.link" class="pm-card-footer">
            <span class="pm-proceed">Proceed</span>
            <span class="pm-arrow">
              <v-icon color="white" small>mdi-arrow-right</v-icon>
            </span>
          </router-link>
        </div>
      </template>
    </div>

    <!-- Quick navigation banner -->
    <div v-if="showQuickNav" class="pm-quicknav">
      <div class="pm-quicknav-icon">
        <v-icon color="primary">mdi-information-outline</v-icon>
      </div>
      <div>
        <div class="pm-quicknav-title">Quick navigation</div>
        <div class="pm-quicknav-sub">
          Access any stage of the workflow quickly and continue your process
          seamlessly.
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { viewPermissions } from '@/utils/roles'

export default {
  name: 'ProcessMenu',
  props: {
    // Card definitions: { src, heading, text, link, roles }
    items: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'Choose the next step in your process',
    },
    subtitle: {
      type: String,
      default:
        'Select an option below to continue with your motor assessment workflow.',
    },
    showQuickNav: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    viewPermissions,
  },
}
</script>

<style scoped>
.pm-panel {
  border-radius: 20px;
  border: 1px solid #e8edf5;
  background: #ffffff;
  padding: 24px 24px 20px;
}

/* Header */
.pm-header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 8px 4px 20px;
}
.pm-search-badge {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(135deg, #034ea2 0%, #0a63c4 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(3, 78, 162, 0.28);
}
.pm-header-text {
  min-width: 0;
}
.pm-title {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
  color: #0f2a55;
  margin: 0;
}
.pm-subtitle {
  font-size: 15px;
  color: #7b8794;
  margin: 4px 0 0;
}

/* Grid — 1 / 2 / 3 / 4 columns */
.pm-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}
@media (min-width: 600px) {
  .pm-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 960px) {
  .pm-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 1264px) {
  .pm-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Card */
.pm-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.pm-card:hover {
  box-shadow: 0 12px 28px rgba(3, 78, 162, 0.12);
  transform: translateY(-2px);
}
.pm-card-body {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 18px;
  flex: 1 1 auto;
}
.pm-icon-badge {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #eaf1fb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pm-card-text {
  min-width: 0;
}
.pm-card-heading {
  font-size: 17px;
  font-weight: 700;
  color: #0f2a55;
  margin: 2px 0 0;
  line-height: 1.25;
}
.pm-card-divider {
  display: block;
  width: 34px;
  height: 3px;
  border-radius: 2px;
  background: #034ea2;
  margin: 8px 0 10px;
}
.pm-card-desc {
  font-size: 13.5px;
  line-height: 1.5;
  color: #6b7684;
  margin: 0;
}

/* Footer action band */
.pm-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f5f8fd;
  border-top: 1px solid #eef2f8;
  text-decoration: none;
  transition: background 0.2s ease;
}
.pm-card-footer:hover {
  background: #eaf1fb;
}
.pm-proceed {
  font-size: 15px;
  font-weight: 700;
  color: #034ea2;
}
.pm-arrow {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #034ea2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(3, 78, 162, 0.3);
  transition: transform 0.2s ease;
}
.pm-card-footer:hover .pm-arrow {
  transform: translateX(3px);
}

/* Quick navigation banner */
.pm-quicknav {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 22px;
  padding: 18px 22px;
  border-radius: 16px;
  background: #f5f8fd;
  border: 1px solid #eef2f8;
}
.pm-quicknav-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eaf1fb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pm-quicknav-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f2a55;
}
.pm-quicknav-sub {
  font-size: 13.5px;
  color: #6b7684;
  margin-top: 2px;
}

@media (max-width: 599px) {
  .pm-panel {
    padding: 18px 14px 16px;
  }
  .pm-title {
    font-size: 21px;
  }
  .pm-subtitle {
    font-size: 13.5px;
  }
  .pm-search-badge {
    width: 52px;
    height: 52px;
    border-radius: 14px;
  }
}
</style>
